import {
  ICategory,
  ICategoryQMOS,
  IDelayConfigQMOS,
  IDictionary,
  IDictionaryNumericKey,
  ITreeCategoryNode
} from "../../types";
import { distinct } from "../../system/utils";

export interface ISelectedCategoryNode {
  categoryID: number;
  result: ITreeCategoryNode | null;
}

export interface ICategoryProcessed {
  tree: ITreeCategoryNode;
  dictionary: IDictionaryNumericKey<ITreeCategoryNode>;
  keywordMap: IDictionary<Set<string>>;
}

const updateDictionary = (dictionary: IDictionary<Set<string>>, keyword: string | null, value: string): void => {
  if (keyword) {
    if (!dictionary[keyword]) {
      dictionary[keyword] = new Set<string>([ value ]);
    } else {
      dictionary[keyword].add(value);
    }
  }
};

const processTreeChildrenNodes = (categories: ICategory[], parent: ITreeCategoryNode, result: ICategoryProcessed, level: number): void => {
  const children = categories.filter(x => x.parentID === parent.categoryID);

  let pathText = "";
  if (level > 0) {
    if (parent.name !== "NA") {
      pathText = parent.path.text !== "" ? `${parent.path.text} ❯ ${parent.name}` : parent.name;
    } else {
      pathText = parent.path.text;
    }
  }



  children.forEach(record => {
    const child = {
      categoryID: record.categoryID,
      code: record.code,
      name: record.name,
      color: record.color,
      keyword: record.keyword,
      path: {
        ids: [...parent.path.ids, record.categoryID],
        text: pathText,
        keyword: record.keyword ? { ...parent.path.keyword, [record.keyword]: record.name } : { ...parent.path.keyword }
      },
      parentID: parent.categoryID,
      children: []
    };

    updateDictionary(result.keywordMap, child.keyword, child.name);
    processTreeChildrenNodes(categories, child, result, level + 1);
    result.dictionary[record.categoryID] = child;

    parent.children.push(child);
  });
};

export const processTreeNodes = (categories: ICategory[]): ICategoryProcessed => {
  const root: ITreeCategoryNode = {
    categoryID: -1,
    code: "root",
    name: "Categories",
    parentID: null,
    keyword: null,
    path: {
      ids: [-1],
      text: "",
      keyword: {}
    },
    children: [],
    color: "transparent"
  };

  const result: ICategoryProcessed = {
    tree: root,
    dictionary: {},
    keywordMap: {}
  };

  const children = categories.filter(x => x.parentID === null);
  children.forEach(record => {
    const child = {
      categoryID: record.categoryID,
      code: record.code,
      name: record.name,
      color: record.color,
      keyword: record.keyword,
      path: {
        ids: [-1, record.categoryID],
        text: "",
        keyword: record.keyword ? { [record.keyword]: record.name } : { }
      },
      parentID: null,
      children: []
    };

    updateDictionary(result.keywordMap, child.keyword, child.name);
    processTreeChildrenNodes(categories, child, result, 0);

    result.dictionary[record.categoryID] = child;

    root.children.push(child);
  });

  return result;
};

export const processQMOSTreeNodes = (data: IDelayConfigQMOS): ICategoryQMOS[] => {

  return data.areaGroup.map(areaGroup => ({
    ...areaGroup,
    keyword: "AREA_GROUP",
    children: data.area.filter(f => f.parentID === areaGroup.id).map(area => {
      const areaRelation = data.relationship.filter(f => f.areaID === area.id);
      const relEquipment = areaRelation.map(r => r.equipmentID).filter(distinct);

      return  {
        ...area,
        keyword: "AREA",
        children: data.equipment.filter(f => relEquipment.find(i => i === f.id)).map(equipment => {
          const equipmentRelation = areaRelation.filter(x => x.equipmentID === equipment.id);
          const relObservation = equipmentRelation.map(r => r.observationID).filter(distinct);

          return {
            ...equipment,
            keyword: "EQUIPMENT",
            children: data.observation.filter(f => relObservation.find(i => i === f.id)).map(observation => {
              const observationRelation = equipmentRelation.filter(x => x.observationID === observation.id);
              const relReason = observationRelation.map(r => r.reasonID).filter(distinct);

              return {
                ...observation,
                keyword: "OBSERVATION",
                children: data.reasons.filter(f => relReason.find(i => i === f.id)).map(reason => {
                  const reasonRelation = observationRelation.filter(x => x.reasonID === reason.id);
                  const relActionCode = reasonRelation.map(r => r.actionCodeID).filter(distinct);

                  return {
                    ...reason,
                    keyword: "REASONS",
                    children: data.actionCode.filter(f => relActionCode.find(i => i === f.id)).map(actionCode => {
                      return {
                        ...actionCode,
                        keyword: "ACTION_CODE",
                        children: []
                      };
                    })
                  };
                })
              };
            })
          };
        })
      };
    })
  }));
};
